import { Button, type ButtonProps } from 'antd'
import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'

export type LinkButtonProps = Omit<ButtonProps, 'href' | 'onClick'> & {
  /** In-app destination, as it would be passed to a router Link. */
  to: string
  /** Runs before the navigation; preventDefault() here cancels it. */
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

/**
 * An antd Button rendered as a real anchor, so ctrl/cmd-click and middle-click
 * open the destination in a new tab the way any other link does. Plain clicks
 * are still routed in-app -- letting the browser follow the href would reload
 * the whole SPA.
 *
 * A disabled LinkButton is inert: antd drops the href, so there is nothing to
 * open in a new tab either.
 */
export function LinkButton({ to, onClick, ...props }: LinkButtonProps) {
  const navigate = useNavigate()

  return (
    <Button
      {...props}
      href={to}
      onClick={(event) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        // Modified and non-primary clicks mean the user asked for a new tab or
        // window; the browser does that better than we can.
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        ) {
          return
        }
        event.preventDefault()
        navigate(to)
      }}
    />
  )
}
